import React from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { NestedOccurrenceCount, Outcome } from '../types';

const outcomeConfigs = {
    [Outcome.FAIL]: {
        description: 'Rule check has failed',
        color: '#D52214', // Red for rejected/fail
    },
    [Outcome.WARNING]: {
        description: 'Rule check is a warning',
        color: '#E0B80E', // Yellow/orange for referred/warning
    },
    [Outcome.PASS]: {
        description: 'Rule check has passed',
        color: '#389E0D', // Green for accepted/pass
    },
};

interface RuleOutcomeChartProps {
    data: NestedOccurrenceCount[];
}

const RuleOutcomeChart: React.FC<RuleOutcomeChartProps> = ({ data }) => {
    // Prepare data for the stacked bar chart
    const prepareChartData = () => {
        // Sort data by Warning + Fail counts (descending)
        const sortedData = [...data].sort((a, b) => {
            const aWarningFail = (a.children || [])
                .filter(
                    (child) =>
                        child.name === Outcome.WARNING ||
                        child.name === Outcome.FAIL,
                )
                .reduce((sum, child) => sum + child.occurrence, 0);

            const bWarningFail = (b.children || [])
                .filter(
                    (child) =>
                        child.name === Outcome.WARNING ||
                        child.name === Outcome.FAIL,
                )
                .reduce((sum, child) => sum + child.occurrence, 0);

            return bWarningFail - aWarningFail; // Sort descending
        });

        // Transform to the format required by Nivo
        return sortedData.map((ruleItem) => {
            // Create base object with rule name
            const barItem: any = {
                rule: ruleItem.label || ruleItem.name,
                ruleId: ruleItem.name,
                total: ruleItem.occurrence || 0,
            };

            // Ensure all outcomes exist with at least 0 value
            Object.values(Outcome).forEach((outcome) => {
                barItem[outcome] = 0;
            });

            // Add actual values from children
            if (ruleItem.children && ruleItem.children.length > 0) {
                ruleItem.children.forEach((child) => {
                    if (
                        Object.values(Outcome).includes(child.name as Outcome)
                    ) {
                        barItem[child.name] = child.occurrence;
                    }
                });
            }

            return barItem;
        });
    };

    const chartData = prepareChartData();

    // Define keys in the specific order we want the stacks
    // Order is reversed: Fail at bottom, then Warning, then Pass at top
    const keys = [Outcome.FAIL, Outcome.WARNING, Outcome.PASS];

    return (
        <div className="chart">
            <ResponsiveBar
                data={chartData}
                keys={keys}
                indexBy="rule"
                margin={{ top: 20, right: 10, bottom: 80, left: 80 }}
                padding={0.3}
                valueScale={{ type: 'linear' }}
                indexScale={{ type: 'band', round: true }}
                colors={({ id }) =>
                    outcomeConfigs[id as Outcome]?.color || '#000000'
                }
                borderColor={{
                    from: 'color',
                    modifiers: [['darker', 1.6]],
                }}
                borderRadius={10}
                axisTop={null}
                axisRight={null}
                axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: -20, // Angled labels for better readability on crowded x-axis
                    legendPosition: 'middle',
                    legendOffset: 50,
                }}
                axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legendPosition: 'middle',
                    legendOffset: -50,
                }}
                labelSkipWidth={12}
                labelSkipHeight={12}
                labelTextColor={{
                    from: 'color',
                    modifiers: [['brighter', 4]],
                }}
                role="application"
                ariaLabel="Rule outcome stacked bar chart"
                barAriaLabel={(e) =>
                    `${e.indexValue}: ${e.id} - ${e.value} occurrences`
                }
                theme={{
                    tooltip: {
                        container: {
                            background: '#333',
                            color: '#fff',
                            fontSize: 12,
                            borderRadius: 2,
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.25)',
                            padding: '5px 9px',
                        },
                    },
                }}
                tooltip={({ id, value, indexValue, color }) => (
                    <div
                        style={{
                            padding: 12,
                            color: '#fff',
                            background: '#333',
                            borderLeft: `4px solid ${color}`,
                        }}
                    >
                        <span style={{ display: 'block', marginBottom: 4 }}>
                            <strong>{indexValue}</strong>
                        </span>
                        <span style={{ display: 'block' }}>
                            <strong>{id}:</strong> {value} occurrences
                        </span>
                        {outcomeConfigs[id as Outcome] && (
                            <span
                                style={{
                                    display: 'block',
                                    fontSize: '0.9em',
                                    marginTop: 4,
                                    opacity: 0.9,
                                }}
                            >
                                {outcomeConfigs[id as Outcome].description}
                            </span>
                        )}
                    </div>
                )}
            />
        </div>
    );
};

export default RuleOutcomeChart;
